import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { StatCard, TrendIndicator } from '../components/StatCard';
import { SearchBar } from '../components/SearchBar';
import { TableFilter } from '../components/TableFilter';
import type { Filter } from '../components/TableFilter';
import { ExportButton } from '../components/ExportButton';
import { ClipboardList, X, Copy, ChevronDown, ChevronUp, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useActivities, customerNames } from '../contexts/ActivityContext';
import { useTasks } from '../contexts/TaskContext';
import { ActivityViewsDropdown } from '../components/ActivityViewsDropdown';
import { subscriptionActivityEvents, subscriptionActivityViews, SubscriptionActivityEvent, SubscriptionActivityKind, SubscriptionActivityView } from '../data/mockSubscriptionActivity';
// Specific subscription events, filterable separately from the broader Event Type.
const SPECIFIC_EVENT_OPTIONS: SubscriptionActivityKind[] = ['Payment method added', 'Payment method updated', 'Subscription paused', 'Subscription resumed', 'Subscription cancelled', 'Shipping address updated'];
// Before/after addresses for the shared address-change events
const ADDRESS_CHANGES: Record<string, {
  before: string;
  after: string;
}> = {
  'sa-003': {
    before: '14 Grey St, South Brisbane QLD 4101',
    after: '7 Oxford St, Bulimba QLD 4171'
  },
  'sa-009': {
    before: '22 Rundle St, Kent Town SA 5067',
    after: '3 Jetty Rd, Glenelg SA 5045'
  }
};
const getReason = (description: string): string => {
  const index = description.toLowerCase().indexOf('reason:');
  return index >= 0 ? description.slice(index + 'reason:'.length).trim() : '—';
};
const buildSubscriptionChanges = (event: SubscriptionActivityEvent) => {
  switch (event.kind) {
    case 'Subscription paused':
      return [{
        field: 'Status',
        before: 'Active',
        after: 'Paused'
      }, {
        field: 'Pause Reason',
        before: '—',
        after: getReason(event.description)
      }];
    case 'Subscription cancelled':
      return [{
        field: 'Status',
        before: 'Active',
        after: 'Cancelled'
      }, {
        field: 'Cancellation Reason',
        before: '—',
        after: getReason(event.description)
      }];
    case 'Subscription resumed':
      return [{
        field: 'Status',
        before: 'Paused',
        after: 'Active'
      }];
    case 'Payment method added':
      return [{
        field: 'Payment method',
        before: '—',
        after: 'Visa ending 4242'
      }];
    case 'Payment method updated':
      return [{
        field: 'Payment method',
        before: 'Visa ending 4242',
        after: 'Mastercard ending 8812'
      }];
    case 'Shipping address updated':
      {
        const address = ADDRESS_CHANGES[event.id] ?? {
          before: 'Previous address',
          after: 'New address'
        };
        return [{
          field: 'Shipping address',
          before: address.before,
          after: address.after
        }];
      }
    default:
      return [];
  }
};
// Specific event per shared event id, used by the Event filter
const subscriptionEventKinds = new Map<string, string>(subscriptionActivityEvents.map(event => [event.id, event.kind.toLowerCase()]));
// Mock data for activity log
const mockActivityLog = [{
  id: '1',
  timestamp: '2026-01-07T14:34:00',
  eventType: 'Subscription',
  activity: 'Subscription paused - Reason: Going on vacation',
  entity: 'SUB-23144',
  entityType: 'subscription',
  source: 'Customer Portal',
  user: 'sarah.j@example.com',
  status: 'Success',
  ipAddress: '192.168.1.1',
  changes: [{
    field: 'Status',
    before: 'Active',
    after: 'Paused'
  }, {
    field: 'Pause Reason',
    before: '—',
    after: 'Going on vacation'
  }]
}, {
  id: 'auto-1',
  timestamp: '2026-01-07T14:31:00',
  eventType: 'Automation',
  activity: 'Automated Tag Added — "Needs Attention" applied by rule "At-Risk Member"',
  entity: 'SUB-23144',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: At-Risk Member',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: '—',
    after: 'Needs Attention'
  }, {
    field: 'Rule',
    before: '—',
    after: 'At-Risk Member'
  }]
}, {
  id: '11',
  timestamp: '2026-01-07T14:28:00',
  eventType: 'Loyalty',
  activity: 'Points earned - 120 points from subscription payment',
  entity: 'MEM-1042',
  entityType: 'member',
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Points Balance',
    before: '2,330',
    after: '2,450'
  }]
}, {
  id: '2',
  timestamp: '2026-01-07T14:15:00',
  eventType: 'Payment',
  activity: 'Payment retry attempt 2/3 - Card declined',
  entity: 'SUB-23138',
  entityType: 'subscription',
  source: 'System',
  user: 'Automated',
  status: 'Failed',
  ipAddress: null,
  changes: [{
    field: 'Retry Count',
    before: '1',
    after: '2'
  }, {
    field: 'Last Retry',
    before: 'Jan 5, 2026',
    after: 'Jan 7, 2026'
  }]
}, {
  id: 'auto-2',
  timestamp: '2026-01-07T14:08:00',
  eventType: 'Automation',
  activity: 'Automated Tag Removed — "New Customer" removed by rule "First-Time Member" (exit criteria met)',
  entity: 'SUB-23141',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: First-Time Member',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: 'New Customer',
    after: '—'
  }, {
    field: 'Rule',
    before: '—',
    after: 'First-Time Member (exit criteria)'
  }]
}, {
  id: '12',
  timestamp: '2026-01-07T14:02:00',
  eventType: 'Tier',
  activity: 'Tier upgraded - Enthusiast to Connoisseur (automatic)',
  entity: 'MEM-1087',
  entityType: 'member',
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tier',
    before: 'Enthusiast',
    after: 'Connoisseur'
  }, {
    field: 'Trigger',
    before: '—',
    after: 'Points threshold reached (500)'
  }]
}, {
  id: '3',
  timestamp: '2026-01-07T13:52:00',
  eventType: 'Order',
  activity: 'Subscription order created - 6 items, $126.50',
  entity: 'ORD-1042',
  entityType: 'order',
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: []
}, {
  id: 'auto-3',
  timestamp: '2026-01-07T13:48:00',
  eventType: 'Automation',
  activity: 'Automated Tag Added — "Loyal" applied by rule "Loyal Member (12+ months)"',
  entity: 'SUB-23141',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: Loyal Member (12+ months)',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: '—',
    after: 'Loyal'
  }, {
    field: 'Rule',
    before: '—',
    after: 'Loyal Member (12+ months)'
  }]
}, {
  id: '13',
  timestamp: '2026-01-07T13:45:00',
  eventType: 'Rewards',
  activity: 'Reward redeemed - Free shipping (250 points)',
  entity: 'MEM-1042',
  entityType: 'member',
  source: 'Customer Portal',
  user: 'sarah.j@example.com',
  status: 'Success',
  ipAddress: '192.168.1.1',
  changes: [{
    field: 'Points Balance',
    before: '2,700',
    after: '2,450'
  }, {
    field: 'Reward',
    before: '—',
    after: 'Free shipping applied to next order'
  }]
}, {
  id: '4',
  timestamp: '2026-01-07T13:30:00',
  eventType: 'Subscription',
  activity: 'Products swapped - Shiraz 2022 → Shiraz 2023',
  entity: 'SUB-23141',
  entityType: 'subscription',
  source: 'Admin Portal',
  user: 'mike@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.5',
  changes: [{
    field: 'Product',
    before: 'Shiraz 2022',
    after: 'Shiraz 2023'
  }]
}, {
  id: '14',
  timestamp: '2026-01-07T13:10:00',
  eventType: 'Loyalty',
  activity: 'Points adjusted - +500 points (Cellar door visit)',
  entity: 'MEM-1234',
  entityType: 'member',
  source: 'Admin Portal',
  user: 'mike@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.5',
  changes: [{
    field: 'Points Balance',
    before: '350',
    after: '850'
  }, {
    field: 'Reason',
    before: '—',
    after: 'Cellar door visit'
  }]
}, {
  id: 'auto-4',
  timestamp: '2026-01-07T12:55:00',
  eventType: 'Automation',
  activity: 'Automated Tag Added — "High Value" applied by rule "High Value Spender"',
  entity: 'SUB-23138',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: High Value Spender',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: '—',
    after: 'High Value'
  }, {
    field: 'Rule',
    before: '—',
    after: 'High Value Spender'
  }]
}, {
  id: '5',
  timestamp: '2026-01-07T12:45:00',
  eventType: 'Cancellation',
  activity: 'Subscription cancelled - Reason: Too expensive, Retention offer: Declined 15% discount',
  entity: 'SUB-23135',
  entityType: 'subscription',
  source: 'Customer Portal',
  user: 'emma.w@example.com',
  status: 'Success',
  ipAddress: '192.168.1.10',
  changes: [{
    field: 'Status',
    before: 'Active',
    after: 'Cancelled'
  }, {
    field: 'Cancellation Reason',
    before: '—',
    after: 'Too expensive'
  }, {
    field: 'Retention Offer',
    before: '—',
    after: 'Declined 15% discount'
  }]
}, {
  id: 'auto-5',
  timestamp: '2026-01-07T12:30:00',
  eventType: 'Automation',
  activity: 'Automated Tag Removed — "Loyal" removed by rule "Loyal Member (12+ months)" (member cancelled)',
  entity: 'SUB-23135',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: Loyal Member (12+ months)',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: 'Loyal',
    after: '—'
  }, {
    field: 'Rule',
    before: '—',
    after: 'Loyal Member (12+ months) (exit criteria)'
  }]
}, {
  id: '15',
  timestamp: '2026-01-07T12:20:00',
  eventType: 'Loyalty',
  activity: 'Bulk adjustment - 500 points added to 8 members (Wine club event)',
  entity: '8 members',
  entityType: 'bulk-members',
  source: 'Admin Portal',
  user: 'chris@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.8',
  changes: [{
    field: 'Action',
    before: '—',
    after: '+500 points to 8 members'
  }, {
    field: 'Reason',
    before: '—',
    after: 'Wine club event'
  }]
}, {
  id: '16',
  timestamp: '2026-01-07T11:55:00',
  eventType: 'Tier',
  activity: 'Tier manually changed - Set to Vintner by admin',
  entity: 'MEM-1004',
  entityType: 'member',
  source: 'Admin Portal',
  user: 'mike@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.5',
  changes: [{
    field: 'Tier',
    before: 'Sommelier',
    after: 'Vintner'
  }, {
    field: 'Reason',
    before: '—',
    after: 'Goodwill gesture'
  }]
}, {
  id: '6',
  timestamp: '2026-01-07T11:20:00',
  eventType: 'Settings',
  activity: 'Payment recovery settings updated - Retry attempts changed from 3 to 4',
  entity: null,
  entityType: null,
  source: 'Admin Portal',
  user: 'chris@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.8',
  changes: [{
    field: 'Retry Attempts',
    before: '3',
    after: '4'
  }]
}, {
  id: 'auto-6',
  timestamp: '2026-01-07T11:10:00',
  eventType: 'Automation',
  activity: 'Automated Tag Added — "Premium" applied by rule "Red Wine Lover"',
  entity: 'SUB-23150',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: Red Wine Lover',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: '—',
    after: 'Premium'
  }, {
    field: 'Rule',
    before: '—',
    after: 'Red Wine Lover'
  }]
}, {
  id: '17',
  timestamp: '2026-01-07T11:05:00',
  eventType: 'Rewards',
  activity: 'Reward redeemed - 15% discount code (1,500 points)',
  entity: 'MEM-1004',
  entityType: 'member',
  source: 'Customer Portal',
  user: 'david.p@example.com',
  status: 'Success',
  ipAddress: '192.168.1.30',
  changes: [{
    field: 'Points Balance',
    before: '17,100',
    after: '15,600'
  }, {
    field: 'Discount Code',
    before: '—',
    after: 'LOYAL15-DPK-0107'
  }]
}, {
  id: '18',
  timestamp: '2026-01-07T10:40:00',
  eventType: 'Tier',
  activity: 'Tier locked - Prevented automatic recalculation',
  entity: 'MEM-1004',
  entityType: 'member',
  source: 'Admin Portal',
  user: 'mike@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.5',
  changes: [{
    field: 'Tier Lock',
    before: 'Unlocked',
    after: 'Locked'
  }]
}, {
  id: 'auto-7',
  timestamp: '2026-01-07T10:22:00',
  eventType: 'Automation',
  activity: 'Automated Tag Added — "VIP" applied by rule "Anniversary Approaching"',
  entity: 'SUB-23130',
  entityType: 'subscription',
  source: 'System',
  user: 'Automation: Anniversary Approaching',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: '—',
    after: 'VIP'
  }, {
    field: 'Rule',
    before: '—',
    after: 'Anniversary Approaching'
  }]
}, {
  id: '7',
  timestamp: '2026-01-07T10:05:00',
  eventType: 'Payment',
  activity: 'Payment recovered - $189.50 after retry 2',
  entity: 'SUB-23130',
  entityType: 'subscription',
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Payment Status',
    before: 'Failed',
    after: 'Paid'
  }, {
    field: 'Amount Recovered',
    before: '$0.00',
    after: '$189.50'
  }]
}, {
  id: '19',
  timestamp: '2026-01-07T09:50:00',
  eventType: 'Loyalty',
  activity: 'Points reset - Balance set to 0 (Program migration)',
  entity: 'MEM-1008',
  entityType: 'member',
  source: 'Admin Portal',
  user: 'chris@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.8',
  changes: [{
    field: 'Points Balance',
    before: '50',
    after: '0'
  }, {
    field: 'Reason',
    before: '—',
    after: 'Program migration'
  }]
}, {
  id: '8',
  timestamp: '2026-01-07T09:30:00',
  eventType: 'System',
  activity: 'Daily subscription processing completed - 12 orders created',
  entity: null,
  entityType: null,
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: []
}, {
  id: 'auto-8',
  timestamp: '2026-01-07T09:25:00',
  eventType: 'Automation',
  activity: 'Automated Tag Added — "New Customer" applied by rule "First-Time Member" (3 members matched)',
  entity: '3 members',
  entityType: 'bulk-members',
  source: 'System',
  user: 'Automation: First-Time Member',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tag',
    before: '—',
    after: 'New Customer'
  }, {
    field: 'Members Matched',
    before: '—',
    after: '3 new subscribers'
  }]
}, {
  id: '20',
  timestamp: '2026-01-07T09:15:00',
  eventType: 'Tier',
  activity: 'Bulk tier change - 5 members moved to Sommelier',
  entity: '5 members',
  entityType: 'bulk-members',
  source: 'Admin Portal',
  user: 'mike@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.5',
  changes: [{
    field: 'Action',
    before: '—',
    after: '5 members moved to Sommelier'
  }, {
    field: 'Reason',
    before: '—',
    after: 'Promotional campaign'
  }]
}, {
  id: '21',
  timestamp: '2026-01-07T08:50:00',
  eventType: 'Rewards',
  activity: 'Reward created - Birthday wine bottle added to catalog',
  entity: null,
  entityType: null,
  source: 'Admin Portal',
  user: 'chris@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.8',
  changes: []
}, {
  id: '9',
  timestamp: '2026-01-07T08:15:00',
  eventType: 'Subscription',
  activity: 'Subscription created - Monthly wine club',
  entity: 'SUB-23150',
  entityType: 'subscription',
  source: 'Customer Portal',
  user: 'john.d@example.com',
  status: 'Success',
  ipAddress: '192.168.1.20',
  changes: []
}, {
  id: '22',
  timestamp: '2026-01-07T08:00:00',
  eventType: 'Loyalty',
  activity: 'Points expired - 200 points expired',
  entity: 'MEM-1055',
  entityType: 'member',
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Points Balance',
    before: '320',
    after: '120'
  }, {
    field: 'Expired Points',
    before: '—',
    after: '200'
  }]
}, {
  id: '10',
  timestamp: '2026-01-07T07:45:00',
  eventType: 'Payment',
  activity: 'Payment failed - Insufficient funds',
  entity: 'SUB-23145',
  entityType: 'subscription',
  source: 'System',
  user: 'Automated',
  status: 'Failed',
  ipAddress: null,
  changes: [{
    field: 'Payment Status',
    before: 'Pending',
    after: 'Failed'
  }, {
    field: 'Failure Reason',
    before: '—',
    after: 'Insufficient funds'
  }]
}, {
  id: '23',
  timestamp: '2026-01-07T07:30:00',
  eventType: 'Rewards',
  activity: 'Reward deactivated - Summer tasting experience',
  entity: null,
  entityType: null,
  source: 'Admin Portal',
  user: 'mike@acmewine.com',
  status: 'Success',
  ipAddress: '10.0.0.5',
  changes: [{
    field: 'Reward Status',
    before: 'Active',
    after: 'Deactivated'
  }]
}, {
  id: '24',
  timestamp: '2026-01-07T07:10:00',
  eventType: 'Tier',
  activity: 'Tier downgraded - Sommelier to Connoisseur (automatic)',
  entity: 'MEM-1055',
  entityType: 'member',
  source: 'System',
  user: 'Automated',
  status: 'Success',
  ipAddress: null,
  changes: [{
    field: 'Tier',
    before: 'Sommelier',
    after: 'Connoisseur'
  }, {
    field: 'Trigger',
    before: '—',
    after: 'Points fell below threshold (2,000)'
  }]
}];
export function OperationsActivityLogPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    getAllNotes
  } = useActivities();
  const {
    getTaskActivityLogEvents
  } = useTasks();
  // Convert ActivityContext notes to activity log format
  const noteEvents = useMemo(() => {
    const allNotes = getAllNotes();
    return allNotes.map((note, index) => {
      const customerName = customerNames[note.customerId] || 'Unknown Customer';
      // Convert date string to ISO timestamp for consistent sorting
      const noteDate = new Date(note.date);
      // Add some time offset based on index to differentiate timestamps
      noteDate.setHours(12, 30 - index, 0, 0);
      return {
        id: `note-${note.id}`,
        timestamp: noteDate.toISOString(),
        eventType: 'Customer Notes',
        activity: `Note added on ${customerName} — "${note.description}"`,
        entity: customerName,
        entityType: 'contact' as string | null,
        source: 'Admin Portal',
        user: note.author || 'Admin',
        status: 'Success',
        ipAddress: null as string | null,
        changes: [] as {
          field: string;
          before: string;
          after: string;
        }[]
      };
    });
  }, [getAllNotes]);
  // Get task events from TaskContext
  const taskEvents = useMemo(() => {
    return getTaskActivityLogEvents();
  }, [getTaskActivityLogEvents]);
  // Shared subscription activity (same events as the dashboard card)
  const sharedSubscriptionEvents = useMemo(() => {
    const now = Date.now();
    return subscriptionActivityEvents.map(event => ({
      id: event.id,
      timestamp: new Date(now - event.hoursAgo * 60 * 60 * 1000).toISOString(),
      eventType: event.eventType as string,
      activity: event.description,
      entity: event.subscriptionId,
      entityType: 'subscription' as string | null,
      source: 'Customer Portal',
      user: event.memberName,
      status: 'Success',
      ipAddress: null as string | null,
      changes: buildSubscriptionChanges(event)
    }));
  }, []);
  // Merge mock data with live notes and task events
  const allActivityEvents = useMemo(() => {
    return [...mockActivityLog, ...sharedSubscriptionEvents, ...noteEvents, ...taskEvents].sort((a, b) => {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }, [sharedSubscriptionEvents, noteEvents, taskEvents]);
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEvent, setSelectedEvent] = useState<(typeof allActivityEvents)[0] | null>(null);
  const [rawDataExpanded, setRawDataExpanded] = useState(false);
  const pageSize = 25;
  // Sort state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({
    key: 'timestamp',
    direction: 'desc'
  });
  // Filter state
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    timestamp: [],
    eventType: [],
    event: [],
    activity: [],
    entity: [],
    source: [],
    user: [],
    status: [],
    date: []
  });
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [customDateRanges, setCustomDateRanges] = useState<Record<string, {
    startDate: string;
    endDate: string;
  }>>({});
  // Saved view currently applied (shared with the dashboard card)
  const [activeView, setActiveView] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const applyView = (view: {
    id: string;
    name: string;
    events: string[];
  }) => {
    const usesSpecificEvents = view.events.length > 0;
    setFilterValues(prev => ({
      ...prev,
      event: usesSpecificEvents ? view.events.map(kind => kind.toLowerCase()) : [],
      eventType: usesSpecificEvents ? [] : ['subscription', 'payment', 'cancellation']
    }));
    setAddedFilters(prev => {
      const next = new Set(prev);
      next.delete('event');
      next.delete('eventType');
      next.add(usesSpecificEvents ? 'event' : 'eventType');
      return next;
    });
    setActiveView({
      id: view.id,
      name: view.name
    });
    setCurrentPage(1);
  };
  const clearView = () => {
    setFilterValues(prev => ({
      ...prev,
      event: [],
      eventType: []
    }));
    setAddedFilters(prev => {
      const next = new Set(prev);
      next.delete('event');
      next.delete('eventType');
      return next;
    });
    setActiveView(null);
  };
  // Apply pre-filters from navigation state (e.g., from Dashboard "View All" button)
  useEffect(() => {
    const state = location.state as {
      filterByNotes?: boolean;
      subscriptionActivityView?: {
        id: string;
        name: string;
        events: SubscriptionActivityKind[];
      };
      eventId?: string;
    } | null;
    if (state?.filterByNotes) {
      setFilterValues(prev => ({
        ...prev,
        eventType: ['customer notes']
      }));
      setAddedFilters(new Set(['eventType']));
      // Clear the state so refreshing doesn't re-apply
      window.history.replaceState({}, document.title);
    }
    if (state?.subscriptionActivityView) {
      applyView(state.subscriptionActivityView);
      if (state.eventId) {
        const match = allActivityEvents.find(event => event.id === state.eventId);
        if (match) setSelectedEvent(match);
      }
      // Clear the state so refreshing doesn't re-apply
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  // Format date and time
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  // Calculate stats
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventsToday = allActivityEvents.filter(event => {
      const eventDate = new Date(event.timestamp);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate.getTime() === today.getTime();
    }).length;
    const subscriptionChanges = allActivityEvents.filter(e => e.eventType === 'Subscription' || e.eventType === 'Cancellation').length;
    const paymentEvents = allActivityEvents.filter(e => e.eventType === 'Payment').length;
    const paymentFailed = allActivityEvents.filter(e => e.eventType === 'Payment' && e.status === 'Failed').length;
    const loyaltyEvents = allActivityEvents.filter(e => e.eventType === 'Loyalty' || e.eventType === 'Tier' || e.eventType === 'Rewards').length;
    const customerNotes = allActivityEvents.filter(e => e.eventType === 'Customer Notes').length;
    return {
      eventsToday,
      subscriptionChanges,
      paymentEvents,
      paymentFailed,
      loyaltyEvents,
      customerNotes
    };
  }, [allActivityEvents]);
  // Available filters
  const availableFilters: Filter[] = [{
    id: 'timestamp',
    label: 'Timestamp',
    options: [{
      id: '1',
      label: 'Today',
      value: 'today'
    }, {
      id: '2',
      label: 'Last 7 days',
      value: 'last-7-days'
    }, {
      id: '3',
      label: 'Last 30 days',
      value: 'last-30-days'
    }, {
      id: '4',
      label: 'This month',
      value: 'this-month'
    }, {
      id: '5',
      label: 'Custom',
      value: 'custom'
    }],
    selectedValues: filterValues.timestamp || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('timestamp'),
    customDateRange: customDateRanges['timestamp']
  }, {
    id: 'eventType',
    label: 'Event Type',
    options: [{
      id: '1',
      label: 'Subscription',
      value: 'subscription'
    }, {
      id: '2',
      label: 'Order',
      value: 'order'
    }, {
      id: '3',
      label: 'Payment',
      value: 'payment'
    }, {
      id: '4',
      label: 'System',
      value: 'system'
    }, {
      id: '5',
      label: 'Cancellation',
      value: 'cancellation'
    }, {
      id: '6',
      label: 'Settings',
      value: 'settings'
    }, {
      id: '7',
      label: 'Loyalty',
      value: 'loyalty'
    }, {
      id: '8',
      label: 'Tier',
      value: 'tier'
    }, {
      id: '9',
      label: 'Rewards',
      value: 'rewards'
    }, {
      id: '10',
      label: 'Customer Notes',
      value: 'customer notes'
    }, {
      id: '11',
      label: 'Task',
      value: 'task'
    }, {
      id: '12',
      label: 'Automation',
      value: 'automation'
    }],
    selectedValues: filterValues.eventType || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('eventType')
  }, {
    id: 'event',
    label: 'Event',
    options: SPECIFIC_EVENT_OPTIONS.map((kind, index) => ({
      id: String(index + 1),
      label: kind,
      value: kind.toLowerCase()
    })),
    selectedValues: filterValues.event || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('event')
  }, {
    id: 'activity',
    label: 'Activity',
    options: [],
    selectedValue: filterValues.activity?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('activity')
  }, {
    id: 'entity',
    label: 'Entity',
    options: [],
    selectedValue: filterValues.entity?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('entity')
  }, {
    id: 'source',
    label: 'Source',
    options: [{
      id: '1',
      label: 'Customer Portal',
      value: 'customer-portal'
    }, {
      id: '2',
      label: 'Admin Portal',
      value: 'admin-portal'
    }, {
      id: '3',
      label: 'API',
      value: 'api'
    }, {
      id: '4',
      label: 'Webhook',
      value: 'webhook'
    }, {
      id: '5',
      label: 'System (Automated)',
      value: 'system'
    }],
    selectedValues: filterValues.source || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('source')
  }, {
    id: 'user',
    label: 'User',
    options: [],
    selectedValue: filterValues.user?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('user')
  }, {
    id: 'status',
    label: 'Status',
    options: [{
      id: '1',
      label: 'Success',
      value: 'success'
    }, {
      id: '2',
      label: 'Failed',
      value: 'failed'
    }, {
      id: '3',
      label: 'Pending',
      value: 'pending'
    }, {
      id: '4',
      label: 'Warning',
      value: 'warning'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }, {
    id: 'date',
    label: 'Date',
    options: [{
      id: '1',
      label: 'Today',
      value: 'today'
    }, {
      id: '2',
      label: 'Last 7 days',
      value: 'last-7-days'
    }, {
      id: '3',
      label: 'Last 30 days',
      value: 'last-30-days'
    }, {
      id: '4',
      label: 'This month',
      value: 'this-month'
    }, {
      id: '5',
      label: 'Custom',
      value: 'custom'
    }],
    selectedValues: filterValues.date || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('date'),
    customDateRange: customDateRanges['date']
  }];
  // Filter events
  const filteredEvents = useMemo(() => {
    let result = allActivityEvents.filter(event => {
      // Timestamp filter (date range)
      if (filterValues.timestamp && filterValues.timestamp.length > 0) {
        const eventDate = new Date(event.timestamp);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - eventDate.getTime()) / (1000 * 60 * 60 * 24));
        const timestampMatch = filterValues.timestamp.some(value => {
          if (value === 'today') return daysDiff === 0;
          if (value === 'last-7-days') return daysDiff <= 7;
          if (value === 'last-30-days') return daysDiff <= 30;
          if (value === 'this-month') {
            return eventDate.getMonth() === now.getMonth() && eventDate.getFullYear() === now.getFullYear();
          }
          if (value === 'custom') {
            const dateRange = customDateRanges['timestamp'];
            if (dateRange && dateRange.startDate && dateRange.endDate) {
              const start = new Date(dateRange.startDate);
              const end = new Date(dateRange.endDate);
              end.setHours(23, 59, 59, 999);
              return eventDate >= start && eventDate <= end;
            }
            return true;
          }
          return false;
        });
        if (!timestampMatch) return false;
      }
      // Event type filter
      if (filterValues.eventType && filterValues.eventType.length > 0) {
        const typeMatch = filterValues.eventType.some(value => event.eventType.toLowerCase() === value.toLowerCase());
        if (!typeMatch) return false;
      }
      // Specific event filter
      if (filterValues.event && filterValues.event.length > 0) {
        const kind = subscriptionEventKinds.get(event.id);
        if (!kind || !filterValues.event.includes(kind)) return false;
      }
      // Activity filter (search type)
      if (filterValues.activity && filterValues.activity.length > 0 && filterValues.activity[0].trim()) {
        const searchValue = filterValues.activity[0].toLowerCase();
        if (!event.activity.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // Entity filter (search type)
      if (filterValues.entity && filterValues.entity.length > 0 && filterValues.entity[0].trim()) {
        const searchValue = filterValues.entity[0].toLowerCase();
        if (!event.entity || !event.entity.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // Source filter
      if (filterValues.source && filterValues.source.length > 0) {
        const sourceMatch = filterValues.source.some(value => {
          const sourceValue = event.source.toLowerCase().replace(/\s+/g, '-');
          return sourceValue === value;
        });
        if (!sourceMatch) return false;
      }
      // User filter (search type)
      if (filterValues.user && filterValues.user.length > 0 && filterValues.user[0].trim()) {
        const searchValue = filterValues.user[0].toLowerCase();
        if (!event.user.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // Status filter
      if (filterValues.status && filterValues.status.length > 0) {
        const statusMatch = filterValues.status.some(value => event.status.toLowerCase() === value.toLowerCase());
        if (!statusMatch) return false;
      }
      // Date filter
      if (filterValues.date && filterValues.date.length > 0) {
        const eventDate = new Date(event.timestamp);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - eventDate.getTime()) / (1000 * 60 * 60 * 24));
        const dateMatch = filterValues.date.some(value => {
          if (value === 'today') return daysDiff === 0;
          if (value === 'last-7-days') return daysDiff <= 7;
          if (value === 'last-30-days') return daysDiff <= 30;
          if (value === 'this-month') {
            return eventDate.getMonth() === now.getMonth() && eventDate.getFullYear() === now.getFullYear();
          }
          if (value === 'custom') {
            const dateRange = customDateRanges['date'];
            if (dateRange && dateRange.startDate && dateRange.endDate) {
              const start = new Date(dateRange.startDate);
              const end = new Date(dateRange.endDate);
              end.setHours(23, 59, 59, 999);
              return eventDate >= start && eventDate <= end;
            }
            return true;
          }
          return false;
        });
        if (!dateMatch) return false;
      }
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return event.activity.toLowerCase().includes(query) || event.entity?.toLowerCase().includes(query) || event.user.toLowerCase().includes(query) || event.eventType.toLowerCase().includes(query);
      }
      return true;
    });
    // Apply sorting
    if (sortConfig) {
      result = [...result].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        switch (sortConfig.key) {
          case 'timestamp':
            aValue = new Date(a.timestamp).getTime();
            bValue = new Date(b.timestamp).getTime();
            break;
          case 'eventType':
            aValue = a.eventType.toLowerCase();
            bValue = b.eventType.toLowerCase();
            break;
          case 'activity':
            aValue = a.activity.toLowerCase();
            bValue = b.activity.toLowerCase();
            break;
          case 'entity':
            aValue = (a.entity || '').toLowerCase();
            bValue = (b.entity || '').toLowerCase();
            break;
          case 'source':
            aValue = a.source.toLowerCase();
            bValue = b.source.toLowerCase();
            break;
          case 'user':
            aValue = a.user.toLowerCase();
            bValue = b.user.toLowerCase();
            break;
          case 'status':
            aValue = a.status.toLowerCase();
            bValue = b.status.toLowerCase();
            break;
          default:
            return 0;
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [allActivityEvents, filterValues, searchQuery, customDateRanges, sortConfig]);
  // Pagination
  const totalPages = Math.ceil(filteredEvents.length / pageSize);
  const paginatedEvents = filteredEvents.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const hasActiveFilters = addedFilters.size > 0 || searchQuery.trim() !== '';
  // Event type badge variant
  const getEventTypeBadge = (type: string) => {
    const badges: Record<string, {
      bg: string;
      text: string;
      label: string;
    }> = {
      Subscription: {
        bg: '#DBEAFE',
        text: '#1E40AF',
        label: 'Subscription'
      },
      Order: {
        bg: '#EDE9FE',
        text: '#6F42C1',
        label: 'Order'
      },
      Payment: {
        bg: '#FEF3C7',
        text: '#92400E',
        label: 'Payment'
      },
      Cancellation: {
        bg: '#FEE2E2',
        text: '#B91C1C',
        label: 'Cancellation'
      },
      Settings: {
        bg: '#F1F5F9',
        text: '#475569',
        label: 'Settings'
      },
      System: {
        bg: '#E2E8F0',
        text: '#334155',
        label: 'System'
      },
      Loyalty: {
        bg: '#D1FAE5',
        text: '#065F46',
        label: 'Loyalty'
      },
      Tier: {
        bg: '#FFEDD5',
        text: '#9A3412',
        label: 'Tier'
      },
      Rewards: {
        bg: '#FCE7F3',
        text: '#9D174D',
        label: 'Rewards'
      },
      'Customer Notes': {
        bg: '#E0F2FE',
        text: '#0369A1',
        label: 'Customer Notes'
      },
      Task: {
        bg: '#CCFBF1',
        text: '#0F766E',
        label: 'Task'
      },
      Automation: {
        bg: '#EDE9FE',
        text: '#6F42C1',
        label: 'Automation'
      }
    };
    return badges[type] || badges.System;
  };
  // Status badge variant
  const getStatusVariant = (status: string): 'success' | 'error' | 'warning' => {
    if (status === 'Success') return 'success';
    if (status === 'Failed') return 'error';
    return 'warning';
  };
  // Handle filter operations
  const handleClearAll = () => {
    setFilterValues({
      timestamp: [],
      eventType: [],
      event: [],
      activity: [],
      entity: [],
      source: [],
      user: [],
      status: [],
      date: []
    });
    setAddedFilters(new Set());
    setSearchQuery('');
    setActiveView(null);
  };
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setFilterValues({
      ...filterValues,
      [filterId]: Array.isArray(value) ? value : [value]
    });
  };
  const handleFilterRemove = (filterId: string) => {
    if (filterId === 'event' || filterId === 'eventType') setActiveView(null);
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.delete(filterId);
    setAddedFilters(newAddedFilters);
    // Also clear the filter values so the table actually unfilters
    setFilterValues(prev => ({
      ...prev,
      [filterId]: []
    }));
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpenFilterDropdowns = new Set(openFilterDropdowns);
    newOpenFilterDropdowns.add(filterId);
    setOpenFilterDropdowns(newOpenFilterDropdowns);
  };
  const handleCustomDateChange = (filterId: string, startDate: string, endDate: string) => {
    setCustomDateRanges(prev => ({
      ...prev,
      [filterId]: {
        startDate,
        endDate
      }
    }));
  };
  const handleAddFilter = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.add(filterId);
    setAddedFilters(newAddedFilters);
  };
  const handleSort = (columnKey: string) => {
    setSortConfig(current => {
      if (!current || current.key !== columnKey) {
        return {
          key: columnKey,
          direction: 'asc'
        };
      }
      if (current.direction === 'asc') {
        return {
          key: columnKey,
          direction: 'desc'
        };
      }
      return null;
    });
  };
  const renderSortIcon = (columnKey: string) => {
    if (sortConfig?.key === columnKey) {
      return sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11652" data-mp="1wcbab1ynanif" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-11653" data-mp="1wdmev3zwzzal" />;
    }
    return <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-11654" data-mp="2a497cef30sy8" />;
  };
  const handleRowClick = (event: (typeof allActivityEvents)[0]) => {
    setSelectedEvent(event);
  };
  const handleCloseDrawer = () => {
    setSelectedEvent(null);
    setRawDataExpanded(false);
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-11655" data-mp="1cmhkuujekkhd">
      {/* NavBar */}
      <div className="mb-6" data-id="element-11656" data-mp="dvs65lomom3w">
        <NavBar tabs={[]} title="Activity Log" subtitle="Track all subscription, loyalty, and system events" data-id="element-11657" data-mp="dz666n9tkj86" />
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8" data-id="element-11658" data-mp="mx04g3r59mxz">
        <StatCard label="Events Today" value={stats.eventsToday} secondaryValue="Last event 2 min ago" trend={<TrendIndicator value={12} label="vs yesterday" direction="up" data-id="element-11660" data-mp="3rbgec6ak6r72" />} data-id="element-11659" data-mp="iw6fr85vo441" />

        <StatCard label="Subscription Changes" value={stats.subscriptionChanges} secondaryValue="Paused, cancelled, updated" data-id="element-11661" data-mp="3mdeuvk980pv2" />

        <StatCard label="Payment Events" value={stats.paymentEvents} secondaryValue="Processed, failed, recovered" badge={stats.paymentFailed > 0 ? {
        text: `${stats.paymentFailed} Failed`,
        variant: 'error'
      } : undefined} data-id="element-11662" data-mp="407ae5a5ki2l" />

        <StatCard label="Loyalty Events" value={stats.loyaltyEvents} secondaryValue="Points, tiers, rewards" data-id="element-11663" data-mp="37m5x2nv5zy6u" />

        <StatCard label="Customer Notes" value={stats.customerNotes} secondaryValue="Notes from team members" data-id="element-11664" data-mp="2tiqi4qimxeji" />
      </div>

      <hr className="my-10 border-[#CBD5E1]" data-id="element-11665" data-mp="2y9yt3qzm57ys" />

      {/* Filter Bar */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg mb-6 p-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-11666" data-mp="2yb9slem0ztk3">
        <div className="flex flex-col gap-4" data-id="element-11667" data-mp="2ojdui3jxw3fv">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-11668" data-mp="2ok5esi836equ">
            <div className="w-full lg:w-80 flex-shrink-0" data-id="element-11669" data-mp="3caj80c2eqjn0">
              <SearchBar placeholder="Search by ID, customer, or action..." value={searchQuery} onChange={setSearchQuery} className="w-full" data-id="element-11670" data-mp="3h8ulcwrwvc9z" />
            </div>

            <div className="flex items-center gap-3 flex-shrink-0 ml-auto" data-id="element-11671" data-mp="32d4vuonioh0i">
              {hasActiveFilters && <button onClick={handleClearAll} className="inline-flex items-center justify-center h-12 w-12 text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] border border-[#CBD5E1] rounded-md transition-all duration-200" title="Clear all filters" aria-label="Clear all filters" data-id="element-11672" data-mp="37c81aa7esp90">
                  <X className="w-5 h-5" data-id="element-11673" data-mp="2bg33krnlkfm" />
                </button>}

              <ExportButton variant="subscriptions" onExport={async type => {
              console.log(`Exporting ${type}`);
            }} data-id="element-11674" data-mp="3tiajqqyucxht" />

              <ActivityViewsDropdown views={subscriptionActivityViews} activeViewId={activeView?.id ?? null} onSelect={(view: SubscriptionActivityView) => applyView(view)} data-id="element-11675" data-mp="3opr3tncolq9u" />

              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} onCustomDateChange={handleCustomDateChange} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-11676" data-mp="3eufqn9gx8r47" />
            </div>
          </div>

          {addedFilters.size > 0 && <div className="w-full" data-id="element-11677" data-mp="1424pioarbg04">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} onCustomDateChange={handleCustomDateChange} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-11678" data-mp="14131lj8n6z0z" />
            </div>}
        </div>
      </div>

      {activeView && <div className="flex items-center gap-2 mb-3 text-[13px] text-[#64748B]" data-id="element-11679" data-mp="1gc415lwe71fn">
          <span data-id="element-11680" data-mp="1laxijpesvcl3">
            Showing view:{' '}
            <span className="font-medium text-[#0F172A]" data-id="element-11681" data-mp="1l9m48vnqamfq">{activeView.name}</span>
          </span>
          <button type="button" onClick={clearView} className="text-[#0EA5E9] font-medium hover:text-[#0284C7] transition-colors" data-id="element-11682" data-mp="nognyrxgbd31">
            Clear
          </button>
        </div>}

      {/* Activity Log Table */}
      {filteredEvents.length > 0 ? <>
          <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden mb-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-11683" data-mp="12kftpvx39150">
            <div className="overflow-x-auto" data-id="element-11684" data-mp="28zgfj8seg2xv">
              <table className="w-full" data-id="element-11685" data-mp="292m8iz45foyv">
                <thead className="bg-[#F1F5F9]" data-id="element-11686" data-mp="2dyte2qsmuyga">
                  <tr data-id="element-11687" data-mp="2e1pc0f4hdn4q">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-40" data-id="element-11688" data-mp="2bkxv22gpa5g3">
                      <button onClick={() => handleSort('timestamp')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-11689" data-mp="27k4btme7detj">
                        Timestamp
                        {renderSortIcon('timestamp')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-36" data-id="element-11690" data-mp="2uja6xvyzxtfd">
                      <button onClick={() => handleSort('eventType')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-11691" data-mp="2qhf8r0cd3w5t">
                        Event Type
                        {renderSortIcon('eventType')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-11692" data-mp="3e8uez479exmc">
                      <button onClick={() => handleSort('activity')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-11693" data-mp="39a9d70cd05vs">
                        Activity
                        {renderSortIcon('activity')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-32" data-id="element-11694" data-mp="3iv91nmje25e3">
                      <button onClick={() => handleSort('entity')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-11695" data-mp="3ntk9xsu1c7u7">
                        Entity
                        {renderSortIcon('entity')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-32" data-id="element-11696" data-mp="3073xidytkier">
                      <button onClick={() => handleSort('source')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-11697" data-mp="352t3p05cgxk1">
                        Source
                        {renderSortIcon('source')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-40" data-id="element-11698" data-mp="2c3nwwo0y76k2">
                      <button onClick={() => handleSort('user')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-11699" data-mp="279be8g7apz9d">
                        User
                        {renderSortIcon('user')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-28" data-id="element-11700" data-mp="2v0grcnluqdfz">
                      <button onClick={() => handleSort('status')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-11701" data-mp="2q01edx5hf7yj">
                        Status
                        {renderSortIcon('status')}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-11702" data-mp="14oswallhnea1">
                  {paginatedEvents.map(event => {
                const eventBadge = getEventTypeBadge(event.eventType);
                return <tr key={event.id} onClick={() => handleRowClick(event)} className={`hover:bg-[#F8FAFC] transition-colors duration-200 cursor-pointer ${event.status === 'Failed' ? 'border-l-4 border-l-[#EF4444]' : ''}`} style={{
                  height: '64px'
                }} data-id="element-11703" data-mp="3lazc6vnovaz9">
                        <td className="px-4 py-4" data-id="element-11704" data-mp="3v2lvzge3eidm">
                          <div className="text-sm text-[#0F172A]" data-id="element-11705" data-mp="3tg5tff4j8mj">
                            {formatDateTime(event.timestamp)}
                          </div>
                          <div className="text-xs text-[#94A3B8] mt-0.5" data-id="element-11706" data-mp="cvfk3zyr7yls">
                            {formatTime(event.timestamp)}
                          </div>
                        </td>
                        <td className="px-4 py-4" data-id="element-11707" data-mp="2emixuy3xxtak">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap" style={{
                      backgroundColor: eventBadge.bg,
                      color: eventBadge.text
                    }} data-id="element-11708" data-mp="2enu9fk3mo5c8">
                            {eventBadge.label}
                          </span>
                        </td>
                        <td className="px-4 py-4" data-id="element-11709" data-mp="z3ur5cqg9fgm">
                          <span className="text-sm text-[#0F172A]" data-id="element-11710" data-mp="z5nyku8w03po">
                            {event.activity}
                          </span>
                        </td>
                        <td className="px-4 py-4" data-id="element-11711" data-mp="6fp8qwmvc2jy">
                          {event.entity ? <button onClick={e => {
                      e.stopPropagation();
                      if (event.entityType === 'member' || event.entityType === 'bulk-members') {
                        navigate('/loyalty/members');
                      } else if (event.entityType === 'contact') {
                        // Find customer ID by name for navigation
                        const custId = Object.entries(customerNames).find(([_, name]) => name === event.entity)?.[0];
                        if (custId) navigate(`/contacts/${custId}`);
                      } else {
                        console.log(`Navigate to ${event.entity}`);
                      }
                    }} className="text-sm text-[#0EA5E9] hover:text-[#0284C7] hover:underline font-medium transition-colors" data-id="element-11712" data-mp="1gci00svmbc3">
                              {event.entity}
                            </button> : <span className="text-sm text-[#94A3B8]" data-id="element-11713" data-mp="2d36bfr84b2z9">—</span>}
                        </td>
                        <td className="px-4 py-4" data-id="element-11714" data-mp="2i29fb8cd335n">
                          <span className="text-sm text-[#475569]" data-id="element-11715" data-mp="2i0y5aqsboz63">
                            {event.source}
                          </span>
                        </td>
                        <td className="px-4 py-4" data-id="element-11716" data-mp="28boah363h2ea">
                          <span className="text-sm text-[#475569]" data-id="element-11717" data-mp="28ad4cwp6fik0">
                            {event.user}
                          </span>
                        </td>
                        <td className="px-4 py-4" data-id="element-11718" data-mp="30tk51m6cp9vy">
                          <Badge variant={getStatusVariant(event.status)} data-id="element-11719" data-mp="64d32x7hmrbr">
                            {event.status}
                          </Badge>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between" data-id="element-11720" data-mp="jw1boqij2xnz">
            <div className="text-[13px] text-[#64748B]" data-id="element-11721" data-mp="3jawlgfb1dnir">
              Showing {(currentPage - 1) * pageSize + 1}-
              {Math.min(currentPage * pageSize, filteredEvents.length)} of{' '}
              {filteredEvents.length} events
            </div>
            <div className="flex items-center gap-2" data-id="element-11722" data-mp="3sa04xke484xs">
              <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" data-id="element-11723" data-mp="150g8cgm9k96">
                Previous
              </button>
              <div className="flex items-center gap-1" data-id="element-11724" data-mp="2x1ptclnddrbw">
                {Array.from({
              length: Math.min(totalPages, 5)
            }, (_, i) => {
              const page = i + 1;
              return <button key={page} onClick={() => setCurrentPage(page)} className={`w-10 h-10 rounded-md text-[13px] font-medium transition-colors duration-200 ${page === currentPage ? 'bg-[#0EA5E9] text-white' : 'text-[#475569] hover:bg-[#F8FAFC]'}`} data-id="element-11725" data-mp="2nav7vjhy6vri">
                        {page}
                      </button>;
            })}
              </div>
              <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200" data-id="element-11726" data-mp="24daimbb9tv6d">
                Next
              </button>
            </div>
          </div>
        </> : <div className="bg-white rounded-lg border border-[#E2E8F0] p-12 text-center shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-11727" data-mp="1s3b2p1ilrnl1">
          <ClipboardList className="w-16 h-16 text-[#CBD5E1] mx-auto mb-4" data-id="element-11728" data-mp="1wv2sh9txppa4" />
          <h3 className="text-xl font-semibold text-[#0F172A] mb-2" data-id="element-11729" data-mp="1i66g50kuhq3p">
            No events found
          </h3>
          <p className="text-[#64748B] mb-6" data-id="element-11730" data-mp="1n5j8g0uhjpdo">
            Try adjusting your filters or date range
          </p>
          {hasActiveFilters && <Button variant="outline" onClick={handleClearAll} data-id="element-11731" data-mp="2arzij928b2py">
              Clear filters
            </Button>}
        </div>}

      {/* Event Detail Drawer */}
      {selectedEvent && <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={handleCloseDrawer} data-id="element-11732" data-mp="21t4f24qkk6qc" />
          <div className="fixed right-0 top-0 bottom-0 w-[400px] bg-white shadow-xl z-50 overflow-y-auto" data-id="element-11733" data-mp="25t66zm6i5lsf">
            {/* Header */}
            <div className="p-6 border-b border-[#E2E8F0]" data-id="element-11734" data-mp="ufki824k1m7f">
              <div className="flex items-start justify-between mb-3" data-id="element-11735" data-mp="uh5cdqmyj34r">
                <div className="flex-1" data-id="element-11736" data-mp="mv9xe3b4ez04">
                  {(() => {
                const badge = getEventTypeBadge(selectedEvent.eventType);
                return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium mb-2" style={{
                  backgroundColor: badge.bg,
                  color: badge.text
                }} data-id="element-11737" data-mp="hzkr7h4lijuu">
                        {badge.label}
                      </span>;
              })()}
                  <h2 className="text-[18px] font-semibold text-[#0F172A] mt-2" data-id="element-11738" data-mp="3vc4qws61371j">
                    {selectedEvent.activity.split(' — ')[0]}
                  </h2>
                </div>
                <button onClick={handleCloseDrawer} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-11739" data-mp="dxg2mwt08df2">
                  <X className="w-5 h-5 text-[#64748B]" data-id="element-11740" data-mp="3hkgeenrvagk1" />
                </button>
              </div>
              <p className="text-[13px] text-[#64748B]" data-id="element-11741" data-mp="3clvbggl8dirw">
                {formatDateTime(selectedEvent.timestamp)} ·{' '}
                {formatTime(selectedEvent.timestamp)}
              </p>
            </div>

            {/* Note Content - Show for Customer Notes */}
            {selectedEvent.eventType === 'Customer Notes' && <div className="p-6 border-b border-[#E2E8F0]" data-id="element-11742" data-mp="2ouodcicwuk15">
                <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-11743" data-mp="2ot3n34xmpn93">
                  Note Content
                </h3>
                <div className="p-3 bg-[#F8FAFC] rounded-md border border-[#E2E8F0]" data-id="element-11744" data-mp="2yjy865z52gsy">
                  <p className="text-[14px] text-[#475569] italic" data-id="element-11745" data-mp="33irmg0m92bu6">
                    "
                    {selectedEvent.activity.split(' — "')[1]?.replace('"', '') || selectedEvent.activity}
                    "
                  </p>
                </div>
              </div>}

            {/* Details Section */}
            <div className="p-6 border-b border-[#E2E8F0]" data-id="element-11746" data-mp="1d8y3cnf6jug6">
              <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-11747" data-mp="1i5y90x84av6r">
                Details
              </h3>
              <div className="space-y-3" data-id="element-11748" data-mp="1aojpmwpqpesn">
                {selectedEvent.entity && <div data-id="element-11749" data-mp="15vyspmbjelkw">
                    <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-11750" data-mp="15unt7yp4jzzl">
                      Entity
                    </div>
                    <button className="text-[14px] text-[#0EA5E9] hover:text-[#0284C7] hover:underline font-medium" data-id="element-11751" data-mp="10v2p79v3l1g0">
                      {selectedEvent.entity}
                    </button>
                  </div>}
                <div data-id="element-11752" data-mp="1omqclht5hudj">
                  <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-11753" data-mp="1om8ai4jt4lxp">Source</div>
                  <div className="text-[14px] text-[#0F172A]" data-id="element-11754" data-mp="1jnn57qpp7c2e">
                    {selectedEvent.source}
                  </div>
                </div>
                <div data-id="element-11755" data-mp="1fla45flwc2by">
                  <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-11756" data-mp="1fo4n12p9hf39">User</div>
                  <div className="text-[14px] text-[#0F172A]" data-id="element-11757" data-mp="8a1isdb83okk">
                    {selectedEvent.user}
                  </div>
                </div>
                <div data-id="element-11758" data-mp="5obm3fjrnulc">
                  <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-11759" data-mp="5nsh0zf7t0oq">Status</div>
                  <Badge variant={getStatusVariant(selectedEvent.status)} data-id="element-11760" data-mp="amv8ti66paer">
                    {selectedEvent.status}
                  </Badge>
                </div>
                {selectedEvent.ipAddress && <div data-id="element-11761" data-mp="oz3j6zxh9ok">
                    <div className="text-[12px] text-[#94A3B8] mb-1" data-id="element-11762" data-mp="o61gjbwz8cr">
                      IP Address
                    </div>
                    <div className="text-[14px] text-[#0F172A] font-mono" data-id="element-11763" data-mp="3j1dpg8a6xy9z">
                      {selectedEvent.ipAddress}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Changes Section */}
            {selectedEvent.changes.length > 0 && <div className="p-6 border-b border-[#E2E8F0]" data-id="element-11764" data-mp="3e4wyz7fir7tq">
                <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-11765" data-mp="304cg6b9ltc08">
                  Changes
                </h3>
                <div className="space-y-3" data-id="element-11766" data-mp="34564v6uveuu6">
                  {selectedEvent.changes.map((change, index) => <div key={index} className="p-3 bg-[#F8FAFC] rounded-md" data-id="element-11767" data-mp="2li6rl66l4myd" data-mpi={index}>
                      <div className="text-[12px] font-medium text-[#64748B] mb-2" data-id="element-11768" data-mp="2gj2isgnkor8k">
                        {change.field}
                      </div>
                      <div className="flex items-center gap-2" data-id="element-11769" data-mp="2ufz6i98e9sl8">
                        <span className="text-[14px] text-[#EF4444] line-through" data-id="element-11770" data-mp="2pi7frijhkcim">
                          {change.before}
                        </span>
                        <span className="text-[#94A3B8]" data-id="element-11771" data-mp="2zhery2ehi145">→</span>
                        <span className="text-[14px] text-[#10B981] font-medium" data-id="element-11772" data-mp="2zgwp2mxbgon9">
                          {change.after}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>}

            {/* Related Events */}
            {selectedEvent.entity && <div className="p-6 border-b border-[#E2E8F0]" data-id="element-11773" data-mp="3n4mx8kbwxa66">
                <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-11774" data-mp="3i8pfqaowxxrt">
                  Related Events
                </h3>
                <button className="text-[14px] text-[#0EA5E9] hover:text-[#0284C7] hover:underline font-medium" data-id="element-11775" data-mp="xgayckdxiq6h">
                  View all events for {selectedEvent.entity} →
                </button>
              </div>}

            {/* Raw Data */}
            <div className="p-6" data-id="element-11776" data-mp="17d5yt8trj0ne">
              <button onClick={() => setRawDataExpanded(!rawDataExpanded)} className="flex items-center justify-between w-full mb-3" data-id="element-11777" data-mp="17c4lexp9y22i">
                <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider" data-id="element-11778" data-mp="ep9zdwct6umx">
                  Raw Data
                </h3>
                {rawDataExpanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-11779" data-mp="jn356oa79mmo" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-11780" data-mp="ogo6ap73awq3" />}
              </button>
              {rawDataExpanded && <div className="relative" data-id="element-11781" data-mp="3n4tsmvdhcd1z">
                  <pre className="p-3 bg-[#F8FAFC] rounded-md text-[12px] text-[#0F172A] overflow-x-auto font-mono" data-id="element-11782" data-mp="3s4pw8ti9m2h0">
                    {JSON.stringify(selectedEvent, null, 2)}
                  </pre>
                  <button onClick={() => copyToClipboard(JSON.stringify(selectedEvent, null, 2))} className="absolute top-2 right-2 p-2 bg-white border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] transition-colors" title="Copy to clipboard" data-id="element-11783" data-mp="3fm3zmeez6uou">
                    <Copy className="w-4 h-4 text-[#64748B]" data-id="element-11784" data-mp="35n4perekaqzq" />
                  </button>
                </div>}
            </div>
          </div>
        </>}
    </div>;
}